<template>
  <div
    ref="root"
    class="rd"
    :data-w="layout"
    :data-density="density"
    :data-bar="barSticky ? 'sticky' : 'flow'"
    :style="rootStyle"
  >
    <div class="rd-main">
      <report-nav :groups="menuGroups" :current="currentKey" :label="$t('report_mobile.title')" @select="openScreen">
        <template #tools>
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain"
            :title="`${$t('report_desktop.shortcuts')} (?)`"
            :aria-label="$t('report_desktop.shortcuts')"
            :aria-expanded="String(helpOpen)"
            @click="helpOpen = !helpOpen"
          >
            <v-icon size="18">mdi-keyboard-outline</v-icon>
          </button>
          <button
            type="button"
            class="ui-text-btn rd-nav__legacy"
            :title="$t('report_desktop.old_view')"
            @click="openLegacy"
          >
            <v-icon size="16" color="#c2410c">mdi-history</v-icon>
            <span class="rd-nav__legacy-label">{{ $t("report_desktop.old_view") }}</span>
          </button>
        </template>
      </report-nav>

      <report-filter-bar
        ref="bar"
        :from="from"
        :to="to"
        :presets="presets"
        :shifts="shifts"
        :show-shifts="Boolean(screen.shifts)"
        :companies="companyList"
        :selected-ids="barCompanyIds"
        :company-mode="companyMode"
        :search="search"
        :show-search="screen.search !== false"
        :loading="loading"
        :can-excel="!screen.noExcel"
        @period="onPeriod"
        @update:shifts="shifts = $event"
        @companies="onCompanies"
        @update:search="search = $event"
        @refresh="reload"
        @print="print"
        @excel="excel"
      ></report-filter-bar>

      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ screen.title }}</h1>
            <p class="rd-head__sub">{{ headerSubtitle }}</p>
          </div>
          <div class="rd-head__right">
            <label v-if="views.length > 1" class="rd-view">
              <span>{{ $t("view") }}</span>
              <select
                class="rd-select rd-select--sm"
                :value="selectedView"
                :aria-label="$t('view')"
                @change="onView(Number($event.target.value))"
              >
                <option v-for="view in views" :key="view.id" :value="view.id">{{ view.name }}</option>
              </select>
            </label>
            <report-kpis :kpis="kpis"></report-kpis>
            <button
              v-if="addMealTarget && addMealTarget !== currentKey"
              type="button"
              class="ui-btn ui-btn--primary"
              :disabled="!isSingleDay"
              :title="isSingleDay ? $t('add_additional_meal') : $t('report_desktop.single_day_only')"
              @click="openScreen(addMealTarget)"
            >
              <v-icon size="16" color="#ffffff">mdi-plus</v-icon>
              {{ $t("add_meals2") }}
            </button>
          </div>
        </div>

        <div v-if="!companiesReady" class="ui-card rd-card rd-loading" aria-busy="true">
          <div v-for="line in 6" :key="line" class="rd-skeleton" :style="{ height: line === 1 ? '36px' : '28px', width: `${100 - ((line * 9) % 31)}%` }"></div>
        </div>
        <report-desktop-legacy
          v-else-if="screen.legacy"
          :key="`${currentKey}-${reloadKey}`"
          :component="screen.component"
          :component-props="legacyProps"
          @switch-tab="onSwitchTab"
          @toast="showToast"
        ></report-desktop-legacy>
        <component
          :is="screen.component"
          v-else
          :key="`${currentKey}-${reloadKey}`"
          v-bind="screenProps"
          :shifts="shifts"
          @kpis="kpis = $event"
          @loading="loading = $event"
          @open-panel="openPanel"
          @open-screen="openScreen"
          @toast="showToast"
        ></component>
      </div>
    </div>

    <report-detail-panel
      :stack="panelStack"
      @close="closePanel"
      @back="panelBack"
      @open="openPanel"
      @open-screen="openScreen"
      @toast="showToast"
    ></report-detail-panel>
    <transition name="rd-fade">
      <div v-if="panelStack.length" class="rd-scrim" @click="closePanel"></div>
    </transition>

    <report-help-popover :open="helpOpen" :shortcuts="shortcuts" @close="helpOpen = false"></report-help-popover>
    <ui-toast :toast="toast" @action="runToastAction"></ui-toast>
  </div>
</template>

<script>
import moment from "moment";
import "./reportDesktop.scss";
import "./screens/group/reportDesktopGroup.scss";
import "./screens/more/reportDesktopMore.scss";
import ReportNav from "../shared/ReportNav.vue";
import ReportHelpPopover from "../shared/ReportHelpPopover.vue";
import ReportKpis from "../shared/ReportKpis.vue";
import ReportFilterBar from "./ReportFilterBar.vue";
import ReportDetailPanel from "./ReportDetailPanel.vue";
import ReportDesktopLegacy from "./screens/ReportDesktopLegacy.vue";
import hasPlural from "../shared/hasPlural";
import hasReportShell from "../shared/hasReportShell";
import hasReportBadges from "./hasReportBadges";
import hasDailyOnlyPeriod from "../shared/hasDailyOnlyPeriod";
import { buildPeriodPresets, formatRange, formatShortDate } from "../shared/periods";
import {
  ADD_MEAL_TARGET,
  DEFAULT_SCREEN,
  LEGACY_TAB_KEYS,
  buildMenus,
  VIEW_MEALS,
  buildScreens,
} from "./reportScreens";
import {
  ALL_SHIFTS,
  LEGACY_UI_VALUE,
  QUERY_KEYS,
  parseIds,
  parseRange,
  parseShifts,
  serializeIds,
  serializeShifts,
} from "./reportQuery";

const MANAGED_QUERY = ["r", "from", "to", "sh", "c", "v"];

/**
 * Izveštaji na računaru: gornja navigacija (grupe izveštaja), zajednička traka filtera (period,
 * firme, smene, pretraga), zaglavlje sa brojkama, izveštaj i panel sa desne strane. Stanje
 * (izveštaj, period, smene, firme, prikaz) ide u adresu, pa se link može poslati i vratiti.
 * Svaki izveštaj je ili stari tab (legacy) ili ekran koji nasleđuje stari tab, pa štampa
 * i Excel rade isto kao do sada. Raspored, panel, prečice i obaveštenja su u hasReportShell.
 */
export default {
  name: "ReportDesktop",
  mixins: [hasReportShell, hasPlural, hasReportBadges, hasDailyOnlyPeriod],
  components: {
    ReportNav,
    ReportHelpPopover,
    ReportKpis,
    ReportFilterBar,
    ReportDetailPanel,
    ReportDesktopLegacy,
  },
  props: {
    /** [from, to] u formatu YYYY-MM-DD */
    value: {
      type: Array,
      required: true,
    },
    selectedView: {
      type: Number,
      default: 1,
    },
    views: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    shifts: [...ALL_SHIFTS],
    /**
     * Koji spiskovi firmi su učitani i imaju početni izbor: sve firme restorana (meal), firme tipa grupa (food) i
     * firme sa ugovorom "više restorana" (multi).
     */
    readySources: { meal: false, food: false, multi: false },
    pendingSearch: "",
  }),
  computed: {
    from() {
      return this.value[0];
    },
    to() {
      return this.value[1] || this.value[0];
    },
    isSingleDay() {
      return this.from === this.to;
    },
    /** Ekran koji radi samo za jedan dan (Naručeno, Naručioci): period se sam prebacuje na dan. */
    isDailyScreen() {
      return Boolean(this.screen && this.screen.daily);
    },
    presets() {
      return buildPeriodPresets();
    },
    screens() {
      return buildScreens((key, params) => this.$t(key, params));
    },
    menus() {
      return buildMenus((key, params) => this.$t(key, params));
    },
    menuGroups() {
      const groups = this.menus[this.selectedView] || [];
      return groups.map((group) => ({
        ...group,
        items: group.items.map((key) => ({
          key,
          ...this.screens[key],
          badge: this.selectedView === VIEW_MEALS ? this.badges[key]?.text || "" : "",
          badgeComplete: Boolean(this.badges[key]?.complete),
        })),
      }));
    },
    menuKeys() {
      return this.menuGroups.flatMap((group) => group.items.map((item) => item.key));
    },
    currentKey() {
      const wanted = this.$route.query[QUERY_KEYS.report];
      return this.menuKeys.includes(wanted) ? wanted : DEFAULT_SCREEN[this.selectedView];
    },
    screen() {
      return this.screens[this.currentKey];
    },
    screenProps() {
      return {
        ...(this.screen.props || {}),
        ...(this.screen.bindSelectedShifts ? { selectedShifts: this.shifts } : {}),
      };
    },
    /** Stari tab dobija izbor smena iz trake samo ako ga ekran koristi (prop shiftFilter u mostu ka starom tabu). */
    legacyProps() {
      return {
        ...(this.screen.props || {}),
        ...(this.screen.shifts ? { shiftFilter: this.shifts } : {}),
      };
    },
    addMealTarget() {
      return ADD_MEAL_TARGET[this.selectedView] || null;
    },
    mealCompanies() {
      return this.$store.state.report.mealCompanies || [];
    },
    /**
     * Spisak firmi koji ekran koristi: "food" su firme tipa grupa (Jela iz grupa), "multi" firme sa ugovorom "više
     * restorana" (Firme koje hrani restoran), inače sve firme restorana. Izbor ide u isti deo store-a koji čita stari
     * tab (za multi isti kao za meal), pa tab ne zna da ga bira zajednička traka.
     */
    companySource() {
      return ["food", "multi"].includes(this.screen.companySource) ? this.screen.companySource : "meal";
    },
    companyList() {
      return this.listOf(this.companySource);
    },
    companiesReady() {
      const source = this.companySource;
      if (source === "multi") {
        return this.readySources.multi;
      }
      return this.readySources.meal && (source !== "food" || this.readySources.food);
    },
    selectedCompanies() {
      const report = this.$store.state.report;
      return (this.companySource === "food" ? report.selectedFoodCompanies : report.selectedMealCompanies) || [];
    },
    selectedMealCompany() {
      const report = this.$store.state.report;
      return this.companySource === "food" ? report.selectedCompany : report.selectedMealCompany;
    },
    companyMode() {
      if (!this.screen.companies) {
        return null;
      }
      return this.screen.companies === "single" ? "single" : "multi";
    },
    barCompanyIds() {
      if (this.companyMode === "single") {
        return this.selectedMealCompany ? [this.selectedMealCompany.id] : [];
      }
      return this.selectedCompanies.map((company) => company.id);
    },
    search: {
      get() {
        return this.$store.state.report.filters.search;
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "search", value });
      },
    },
    formatLocale() {
      const config = this.getCurrentLanguageConfig();
      return config.script === "latin" && config.locale === "sr-RS" ? "sr-Latn-RS" : config.locale;
    },
    periodLong() {
      if (this.from !== this.to) {
        return formatRange(this.from, this.to);
      }
      const weekday = new Intl.DateTimeFormat(this.formatLocale, { weekday: "long" }).format(
        moment(this.from, "YYYY-MM-DD").toDate()
      );
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${formatShortDate(this.from, true)}`;
    },
    shiftsInline() {
      const list = [...this.shifts].sort((a, b) => a - b);
      if (list.length === 1) {
        return this.$t("report_mobile.shift_inline", { n: list[0] });
      }
      const contiguous = list.length > 2 && list[list.length - 1] - list[0] + 1 === list.length;
      return this.$t("report_mobile.shifts_inline", {
        list: contiguous ? `${list[0]}–${list[list.length - 1]}` : list.join(", "),
      });
    },
    companiesInline() {
      if (this.companyMode === "single") {
        return this.selectedMealCompany ? this.toLatin(this.selectedMealCompany.name) : "";
      }
      if (this.selectedCompanies.length === 1) {
        return this.toLatin(this.selectedCompanies[0].name);
      }
      return this.pluralT("report_mobile.companies_n", this.selectedCompanies.length);
    },
    headerSubtitle() {
      const parts = [this.periodLong];
      if (this.screen.shifts) {
        parts.push(this.shiftsInline);
      }
      if (this.companyMode) {
        parts.push(this.companiesInline);
      }
      return parts.filter(Boolean).join(" · ");
    },
    /** Stanje koje ide u adresu (period "danas" i podrazumevane vrednosti se izostavljaju). */
    urlQuery() {
      const today = moment().format("YYYY-MM-DD");
      const query = { r: this.currentKey, v: String(this.selectedView) };
      if (!(this.from === today && this.to === today)) {
        query.from = this.from;
        query.to = this.to;
      }
      const shifts = serializeShifts(this.shifts);
      if (shifts) {
        query.sh = shifts;
      }
      if (this.companyMode === "single" && this.selectedMealCompany) {
        query.c = String(this.selectedMealCompany.id);
      } else if (this.companyMode === "multi") {
        const ids = serializeIds(this.barCompanyIds, this.companyList.length);
        if (ids) {
          query.c = ids;
        }
      }
      return query;
    },
  },
  watch: {
    urlQuery: {
      handler(query) {
        this.writeUrl(query);
      },
      deep: true,
    },
    currentKey(key, previous) {
      if (key === previous) {
        return;
      }
      this.kpis = [];
      this.loading = false;
      this.closePanel();
      this.search = this.pendingSearch;
      this.pendingSearch = "";
      window.scrollTo(0, 0);
      this.ensureSingleCompany();
    },
    companyMode() {
      this.ensureSingleCompany();
    },
    companySource(source) {
      // meal i multi dele izbor u store-u: kad ga je poslednji postavio drugi izvor, proverava se prema novom spisku
      if (source !== "food" && this.selectionOwner && this.selectionOwner !== source) {
        this.readySources[source] = false;
      }
      this.setupCompanies();
    },
    selectedView() {
      this.closePanel();
    },
    "$store.state.report.mealCompanies"() {
      this.ensureSingleCompany();
    },
    "$store.state.report.foodCompanies"() {
      this.ensureSingleCompany();
    },
    "$store.state.report.restaurantMultiCompanies"() {
      this.ensureSingleCompany();
    },
  },
  created() {
    const { query } = this.$route;
    let range = parseRange(query[QUERY_KEYS.from], query[QUERY_KEYS.to]);
    // Dnevni ekran otvoren sa dužim periodom (npr. iz adrese): odmah jedan dan
    const day = this.isDailyScreen ? this.startDailyScreen(range ? range[0] : this.from, range ? range[1] : this.to) : null;
    if (day) {
      range = [day, day];
    }
    if (range && (range[0] !== this.from || range[1] !== this.to)) {
      this.$emit("input", range);
    }
    this.$store.commit("report/SET_DATE_RANGE", {
      from: range ? range[0] : this.from,
      to: range ? range[1] : this.to,
    });
    const shifts = parseShifts(query[QUERY_KEYS.shifts]);
    if (shifts) {
      this.shifts = shifts;
    }
    this.search = "";
    this.selectionOwner = null; // izvor (meal/multi) koji je poslednji postavio izbor firmi; ne treba da bude reaktivan
    if (this.companySource === "multi") {
      this.setupCompanies("multi");
    } else {
      this.setupCompanies("meal");
      if (this.companySource === "food") {
        this.setupCompanies("food");
      }
    }
  },
  mounted() {
    this.writeUrl(this.urlQuery);
  },
  beforeDestroy() {
    this.search = "";
  },
  methods: {
    // ------------------------------------------------------------ firme
    /**
     * Učitava spisak firmi izvora (sve firme restorana, firme tipa grupa ili firme "više restorana") i postavlja
     * početni izbor: iz adrese, inače sve firme (jedna firma: prva; za "više restorana" uvek prva, kao u starom
     * izgledu). Izbor koji nije u spisku (npr. ostao iz drugog prikaza) se odbacuje. Svaki izvor se postavlja
     * jednom; posle toga izbor ostaje kakav jeste.
     */
    async setupCompanies(source = this.companySource) {
      if (this.readySources[source]) {
        this.ensureSingleCompany();
        return;
      }
      if (source === "food") {
        await this.loadFoodCompanies();
      } else if (source === "multi") {
        if (!this.listOf("multi").length) {
          await this.$store.dispatch("report/fetchRestaurantMultiCompanies");
        }
      } else if (!this.mealCompanies.length) {
        await this.$store.dispatch("report/fetchMealCompanies");
      }
      const list = this.listOf(source);
      const ids = parseIds(this.$route.query[QUERY_KEYS.companies]);
      const fromUrl = list.filter((company) => ids.includes(company.id));
      // meal i multi dele izbor u store-u, a adresa još nosi izbor iz prethodnog prikaza: kreće se od podrazumevanog
      const ownerChanged = source !== "food" && Boolean(this.selectionOwner) && this.selectionOwner !== source;
      if (ownerChanged) {
        this.commitCompanies(source, []);
        this.commitSingleCompany(source, null);
      } else if (fromUrl.length && source === this.companySource) {
        // Adresa se odnosi na izvor ekrana koji je otvoren, ne na drugi spisak firmi.
        if (this.companyMode === "single") {
          this.commitSingleCompany(source, fromUrl[0]);
        } else {
          this.commitCompanies(source, fromUrl);
        }
      }
      const selected = this.selectedOf(source);
      const valid = selected.filter((company) => list.some((item) => item.id === company.id));
      if (valid.length !== selected.length) {
        this.commitCompanies(source, valid);
      }
      if (!valid.length && list.length) {
        this.commitCompanies(source, source === "multi" ? [list[0]] : [...list]);
      }
      const single = source === "food" ? this.$store.state.report.selectedCompany : this.$store.state.report.selectedMealCompany;
      if (single && !list.some((item) => item.id === single.id)) {
        this.commitSingleCompany(source, null);
      }
      this.readySources[source] = true;
      if (source !== "food") {
        this.selectionOwner = source;
      }
      this.ensureSingleCompany();
    },
    /** Firme tipa grupa: stranica ih već traži pri promeni prikaza, pa se čeka taj poziv da se ne duplira. */
    async loadFoodCompanies() {
      if (this.$store.state.report.loading.fetchFoodCompanies) {
        await new Promise((resolve) => {
          const stop = this.$watch(
            () => this.$store.state.report.loading.fetchFoodCompanies,
            (loading) => {
              if (!loading) {
                stop();
                resolve();
              }
            }
          );
        });
      }
      const restaurantId = this.$store.state.restaurant?.id;
      if (!this.$store.state.report.foodCompanies.length && restaurantId) {
        await this.$store.dispatch("report/fetchFoodCompanies", {
          date_from: this.from,
          date_to: this.to,
          restaurant_id: restaurantId,
        });
      }
    },
    listOf(source) {
      const report = this.$store.state.report;
      if (source === "food") {
        return report.foodCompanies || [];
      }
      if (source === "multi") {
        return (report.restaurantMultiCompanies || []).map((company) => ({
          ...company,
          name: this.toLatin(company.name || company.company || ""),
        }));
      }
      return this.mealCompanies;
    },
    selectedOf(source) {
      const report = this.$store.state.report;
      return (source === "food" ? report.selectedFoodCompanies : report.selectedMealCompanies) || [];
    },
    commitCompanies(source, companies) {
      this.$store.commit(
        source === "food" ? "report/SET_SELECTED_FOOD_COMPANIES" : "report/SET_SELECTED_MEAL_COMPANIES", // multi dele izbor sa meal
        companies
      );
    },
    commitSingleCompany(source, company) {
      this.$store.commit(
        source === "food" ? "report/SET_SELECTED_COMPANY" : "report/SET_SELECTED_MEAL_COMPANY",
        company
      );
    },
    /** Izveštaji za jednu firmu (Radnici) odmah dobiju firmu, da se ne otvore prazni. */
    ensureSingleCompany() {
      if (this.companyMode === "single" && !this.selectedMealCompany && this.companyList.length) {
        this.commitSingleCompany(this.companySource, this.companyList[0]);
      }
    },
    onCompanies(ids) {
      const source = this.companySource;
      if (this.companyMode === "single") {
        this.commitSingleCompany(source, this.companyList.find((item) => ids.includes(item.id)) || null);
        return;
      }
      this.commitCompanies(source, this.companyList.filter((company) => ids.includes(company.id)));
    },

    // ------------------------------------------------------------ period, prikaz, izveštaj
    onPeriod({ from, to }) {
      if (from === this.from && to === this.to) {
        return;
      }
      this.$emit("input", [from, to]);
    },
    setDailyPeriod(range) {
      this.onPeriod(range);
    },
    onView(view) {
      if (view !== this.selectedView) {
        this.$emit("update:selectedView", view);
      }
    },
    shiftDay(step) {
      const move = (date) => moment(date, "YYYY-MM-DD").add(step, "day").format("YYYY-MM-DD");
      this.onPeriod({ from: move(this.from), to: move(this.to) });
    },
    openScreen(key, { search = "" } = {}) {
      if (!this.menuKeys.includes(key)) {
        return;
      }
      if (key === this.currentKey) {
        this.search = search;
        return;
      }
      this.pendingSearch = search;
      this.$router
        .push({ query: { ...this.$route.query, [QUERY_KEYS.report]: key } })
        .catch(() => {});
    },
    onSwitchTab(index) {
      const key = (LEGACY_TAB_KEYS[this.selectedView] || [])[index];
      if (key) {
        this.openScreen(key);
      }
    },
    writeUrl(query) {
      const current = this.$route.query;
      const kept = Object.keys(current).reduce((acc, key) => {
        if (!MANAGED_QUERY.includes(key)) {
          acc[key] = current[key];
        }
        return acc;
      }, {});
      const next = { ...kept, ...query };
      const same =
        Object.keys(next).length === Object.keys(current).length &&
        Object.keys(next).every((key) => String(current[key]) === String(next[key]));
      if (!same) {
        this.$router.replace({ query: next }).catch(() => {});
      }
    },

    // ------------------------------------------------------------ radnje
    print() {
      this.$store.commit("SET_PRINTING");
    },
    excel() {
      this.$store.commit("SET_EXCEL");
    },
    reload() {
      this.closePanel();
      this.reloadKey += 1;
      this.$store.commit("report/SET_RELOAD");
    },
    /** Stara stranica (tabovi): ?ui=stari, dok se novi izgled ne potvrdi. */
    openLegacy() {
      this.$router.replace({ query: { ...this.$route.query, [QUERY_KEYS.legacy]: LEGACY_UI_VALUE } }).catch(() => {});
    },
  },
};
</script>
